import { FormEvent, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Calculator,
  Info,
  PackagePlus,
  Plus,
  Save,
  Trash2,
  X,
} from 'lucide-react';

import ActionButton from '../../../components/Buttons/ActionButton';
import DatePicker from '../../../components/Forms/DatePicker/DatePicker';
import InputField from '../../../components/Forms/Input/InputField';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import TextAreaField from '../../../components/Forms/Input/TextAreaField';
import FormModal from '../../../components/Modals/FormModal';
import RestaurantFormHelp from '../shared/RestaurantFormHelp';
import QuickAddSupplier from '../../../components/QuickAdd/Supplier/QuickAddSupplier';
import RawMaterialQuickCreateModal from './RawMaterialQuickCreateModal';
import PurchaseLineConversionHint from './PurchaseLineConversionHint';

import { IProductUnit } from '../../../types/productUnit/productUnitInterfaces';
import {
  RestaurantPurchaseOrderDetailForm,
  RestaurantPurchaseOrderForm,
} from '../../../types/restaurant/RestaurantPurchaseOrderTypes';
import { RestaurantRawMaterialDTO } from '../../../types/restaurant/RestaurantRawMaterialTypes';
import { RestaurantRawMaterialCategoryDTO } from '../../../types/restaurant/RestaurantRawMaterialCategoryTypes';
import { RestaurantUnitConversionDTO } from '../../../types/restaurant/RestaurantUnitConversionTypes';
import { IDropdownSupplier } from '../../../types/supplier/supplierInterfaces';
import { IPaymentConditionDropdown } from '../../../types/paymentConditions/paymentConditionInterfaces';
import { IDropdownDTO as TermDropdownDTO } from '../../../types/term/termInterfaces';

import { formatMoney } from '../../../utils/decimalValue';
import {
  calculateRestaurantPurchaseDueDate,
  calculateRestaurantPurchaseTermAmount,
  withRestaurantPurchaseTermAmount,
} from './restaurantPurchasePayment';

interface Props {
  isOpen: boolean;
  form: RestaurantPurchaseOrderForm;
  suppliers: IDropdownSupplier[];
  rawMaterials: RestaurantRawMaterialDTO[];
  categories: RestaurantRawMaterialCategoryDTO[];
  units: IProductUnit[];
  conversions: RestaurantUnitConversionDTO[];
  paymentConditions: IPaymentConditionDropdown[];
  terms: TermDropdownDTO[];
  saving: boolean;
  loadingOptions: boolean;
  onCreateRawMaterial: (input: {
    name: string;
    categoryId: number;
    unitId: number;
  }) => Promise<RestaurantRawMaterialDTO | null>;
  onCreateCategory: (
    name: string
  ) => Promise<RestaurantRawMaterialCategoryDTO | null>;
  onReloadSuppliers: () => void | Promise<void>;
  onClose: () => void;
  onChange: <
    K extends keyof Omit<RestaurantPurchaseOrderForm, 'id' | 'details'>
  >(
    field: K,
    value: RestaurantPurchaseOrderForm[K]
  ) => void;
  onDetailChange: <
    K extends keyof Omit<RestaurantPurchaseOrderDetailForm, 'key'>
  >(
    key: number,
    field: K,
    value: RestaurantPurchaseOrderDetailForm[K]
  ) => void;
  onAddDetail: () => void;
  onRemoveDetail: (key: number) => void;
  onSave: () => void;
}

const toNumber = (value: number | string | null | undefined) =>
  Number(value || 0);

const calculateDetailTotal = (detail: RestaurantPurchaseOrderDetailForm) =>
  toNumber(detail.quantity) * toNumber(detail.unitCost) -
  toNumber(detail.discountAmount) +
  toNumber(detail.taxAmount);

export const compatiblePurchaseUnits = (
  rawMaterialId: number | '',
  rawMaterials: RestaurantRawMaterialDTO[],
  units: IProductUnit[],
  conversions: RestaurantUnitConversionDTO[]
) => {
  const material = rawMaterials.find(
    (item) => item.id === Number(rawMaterialId)
  );

  if (!material) return [];

  const graph = new Map<number, Set<number>>();

  conversions
    .filter(
      (rule) =>
        rule.active && (rule.global || rule.rawMaterialId === material.id)
    )
    .forEach((rule) => {
      if (!graph.has(rule.fromUnitId)) {
        graph.set(rule.fromUnitId, new Set());
      }

      if (!graph.has(rule.toUnitId)) {
        graph.set(rule.toUnitId, new Set());
      }

      graph.get(rule.fromUnitId)!.add(rule.toUnitId);
      graph.get(rule.toUnitId)!.add(rule.fromUnitId);
    });

  const connected = new Set<number>([material.unitId]);
  const pending = [material.unitId];

  while (pending.length > 0) {
    const currentUnitId = pending.shift();

    if (currentUnitId == null) continue;

    graph.get(currentUnitId)?.forEach((unitId) => {
      if (!connected.has(unitId)) {
        connected.add(unitId);
        pending.push(unitId);
      }
    });
  }

  return units.filter((unit) => connected.has(unit.id));
};

const RestaurantPurchaseOrderFormModal = ({
  isOpen,
  form,
  suppliers,
  rawMaterials,
  categories,
  units,
  conversions,
  paymentConditions,
  terms,
  saving,
  loadingOptions,
  onCreateRawMaterial,
  onCreateCategory,
  onReloadSuppliers,
  onClose,
  onChange,
  onDetailChange,
  onAddDetail,
  onRemoveDetail,
  onSave,
}: Props) => {
  // Alta de materia prima al vuelo: guarda para qué línea del detalle se abrió.
  const [quickCreateForKey, setQuickCreateForKey] = useState<number | null>(
    null
  );
  const [quickSaving, setQuickSaving] = useState(false);

  const applyCreatedMaterial = (
    detailKey: number,
    material: RestaurantRawMaterialDTO
  ) => {
    onDetailChange(detailKey, 'rawMaterialId', material.id);
    onDetailChange(detailKey, 'unitId', material.unitId);
    onDetailChange(detailKey, 'unitCost', material.cost ?? 0);
  };

  const handleQuickCreate = async (input: {
    name: string;
    categoryId: number;
    unitId: number;
  }) => {
    if (quickCreateForKey == null) return;
    setQuickSaving(true);
    try {
      const created = await onCreateRawMaterial(input);
      if (created) {
        applyCreatedMaterial(quickCreateForKey, created);
        setQuickCreateForKey(null);
      }
    } finally {
      setQuickSaving(false);
    }
  };
  // =====================================================
  // 🔹 Validaciones
  // =====================================================

  const errors = useMemo(() => {
    const nextErrors: Record<string, string> = {};

    if (!form.supplierId) {
      nextErrors.supplierId = 'Seleccione un proveedor.';
    }

    if (!form.purchaseDate) {
      nextErrors.purchaseDate = 'Indique la fecha de compra.';
    }

    if (!form.paymentCondition) {
      nextErrors.paymentCondition = 'Seleccione una condición de pago.';
    } else if (form.paymentCondition.code === 'CREDITO') {
      if (!form.paymentCondition.termId || !form.paymentCondition.termCode) {
        nextErrors.paymentTerm = 'Seleccione la unidad del plazo.';
      }

      const creditAmount =
        form.paymentCondition.termCode === '01'
          ? form.paymentCondition.creditDays
          : form.paymentCondition.termCode === '02'
          ? form.paymentCondition.creditMonths
          : form.paymentCondition.termCode === '03'
          ? form.paymentCondition.creditYears
          : null;

      if (creditAmount == null || Number(creditAmount) <= 0) {
        nextErrors.paymentTermAmount = 'El plazo debe ser mayor que cero.';
      }

      if (!form.paymentCondition.creditDueDate) {
        nextErrors.creditDueDate = 'Indique la fecha de vencimiento.';
      } else if (
        form.purchaseDate &&
        form.paymentCondition.creditDueDate < form.purchaseDate
      ) {
        nextErrors.creditDueDate =
          'Debe ser igual o posterior a la fecha de compra.';
      }
    }

    if (form.notes.length > 500) {
      nextErrors.notes = 'Máximo 500 caracteres.';
    }

    if (form.details.length === 0) {
      nextErrors.details = 'Agregue al menos una materia prima.';
    }

    const selectedMaterialIds = new Set<number>();

    form.details.forEach((detail) => {
      const prefix = `detail-${detail.key}`;
      const materialId = Number(detail.rawMaterialId);

      if (!detail.rawMaterialId) {
        nextErrors[`${prefix}-rawMaterialId`] = 'Seleccione una materia prima.';
      } else if (selectedMaterialIds.has(materialId)) {
        nextErrors[`${prefix}-rawMaterialId`] =
          'La materia prima está duplicada.';
      } else {
        selectedMaterialIds.add(materialId);
      }

      if (detail.quantity === '' || Number(detail.quantity) <= 0) {
        nextErrors[`${prefix}-quantity`] = 'Debe ser mayor que cero.';
      }

      if (!detail.unitId) {
        nextErrors[`${prefix}-unitId`] = 'Seleccione una unidad.';
      }

      if (detail.unitCost === '' || Number(detail.unitCost) < 0) {
        nextErrors[`${prefix}-unitCost`] = 'No puede ser negativo.';
      }

      if (detail.discountAmount === '' || Number(detail.discountAmount) < 0) {
        nextErrors[`${prefix}-discount`] = 'No puede ser negativo.';
      }

      if (detail.taxAmount === '' || Number(detail.taxAmount) < 0) {
        nextErrors[`${prefix}-tax`] = 'No puede ser negativo.';
      }

      const subtotal = toNumber(detail.quantity) * toNumber(detail.unitCost);

      if (toNumber(detail.discountAmount) > subtotal) {
        nextErrors[`${prefix}-discount`] = 'Supera el subtotal del detalle.';
      }

      if (
        detail.expirationDate &&
        form.purchaseDate &&
        detail.expirationDate < form.purchaseDate
      ) {
        nextErrors[`${prefix}-expiration`] =
          'Debe ser igual o posterior a la compra.';
      }

      if (detail.lotNumber.length > 100) {
        nextErrors[`${prefix}-lot`] = 'Máximo 100 caracteres.';
      }
    });

    return nextErrors;
  }, [form]);

  const hasErrors = Object.keys(errors).length > 0;

  // =====================================================
  // 🔹 Totales
  // =====================================================

  const estimatedTotal = useMemo(
    () =>
      form.details.reduce(
        (total, detail) => total + calculateDetailTotal(detail),
        0
      ),
    [form.details]
  );

  // =====================================================
  // 🔹 Eventos
  // =====================================================

  const changeMaterial = (
    detail: RestaurantPurchaseOrderDetailForm,
    rawMaterialId: number
  ) => {
    const material = rawMaterials.find((item) => item.id === rawMaterialId);

    onDetailChange(detail.key, 'rawMaterialId', rawMaterialId);

    onDetailChange(detail.key, 'unitId', material?.unitId ?? '');

    onDetailChange(detail.key, 'unitCost', material?.cost ?? 0);
  };

  const changePaymentCondition = (conditionId: number) => {
    const condition = paymentConditions.find((item) => item.id === conditionId);

    if (!condition) return;

    onChange('paymentCondition', {
      id: condition.id,
      name: condition.name,
      code: condition.code ?? 'OTRO',
      creditDueDate: null,
      creditDays: null,
      creditMonths: null,
      creditYears: null,
      termId: null,
      termName: null,
      termCode: null,
    });
  };

  const changePaymentTerm = (termId: number) => {
    const term = terms.find((item) => item.id === termId);
    if (!term || !form.paymentCondition) return;

    const nextCondition = {
      ...form.paymentCondition,
      termId: term.id,
      termName: term.name,
      termCode: term.code,
      creditDays: null,
      creditMonths: null,
      creditYears: null,
    };
    const amount = nextCondition.creditDueDate
      ? calculateRestaurantPurchaseTermAmount(
          form.purchaseDate,
          nextCondition.creditDueDate,
          term.code
        )
      : null;

    onChange(
      'paymentCondition',
      withRestaurantPurchaseTermAmount(nextCondition, amount)
    );
  };

  const changePaymentTermAmount = (value: number | '') => {
    const condition = form.paymentCondition;
    if (!condition) return;

    const amount = value === '' ? null : value;
    const nextCondition = withRestaurantPurchaseTermAmount(condition, amount);

    onChange('paymentCondition', {
      ...nextCondition,
      creditDueDate: calculateRestaurantPurchaseDueDate(
        form.purchaseDate,
        condition.termCode,
        amount
      ),
    });
  };

  const changePurchaseDate = (purchaseDate: string) => {
    onChange('purchaseDate', purchaseDate);

    const condition = form.paymentCondition;
    if (!condition || condition.code !== 'CREDITO') return;

    const amount =
      condition.termCode === '01'
        ? condition.creditDays
        : condition.termCode === '02'
        ? condition.creditMonths
        : condition.termCode === '03'
        ? condition.creditYears
        : null;

    if (amount == null) return;

    onChange('paymentCondition', {
      ...condition,
      creditDueDate: calculateRestaurantPurchaseDueDate(
        purchaseDate,
        condition.termCode,
        amount
      ),
    });
  };

  const changeCreditDueDate = (creditDueDate: string) => {
    const condition = form.paymentCondition;
    if (!condition) return;

    const amount = calculateRestaurantPurchaseTermAmount(
      form.purchaseDate,
      creditDueDate,
      condition.termCode
    );

    onChange('paymentCondition', {
      ...withRestaurantPurchaseTermAmount(condition, amount),
      creditDueDate: creditDueDate || null,
    });
  };

  const save = () => {
    if (saving || loadingOptions || hasErrors) return;

    onSave();
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    save();
  };

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <FormModal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      title={
        form.id ? 'Editar compra en borrador' : 'Nueva compra de materia prima'
      }
      maxWidth="max-w-7xl"
      headerExtra={<RestaurantFormHelp topic="purchaseOrder" />}
    >
      <form onSubmit={submit} className="space-y-5">
        {/* =====================================================
            🔹 Información del proceso
        ===================================================== */}

        <div
          className="
            flex
            items-start
            gap-3

            rounded-xl

            border
            border-primary/15

            bg-primary/5

            p-4

            dark:border-primary/25
            dark:bg-primary/10
          "
        >
          <span
            className="
              flex
              shrink-0
              items-center
              justify-center

              rounded-lg

              bg-primary/10

              p-2

              text-primary

              dark:bg-primary/15
            "
          >
            <Info className="h-5 w-5" />
          </span>

          <div>
            <p className="text-sm font-semibold text-black dark:text-white">
              La compra se guardará como borrador
            </p>

            <p className="mt-1 text-sm text-bodydark2 dark:text-bodydark">
              El inventario se actualizará únicamente cuando la compra sea
              confirmada desde el listado.
            </p>
          </div>
        </div>

        {/* =====================================================
            🔹 Información general
        ===================================================== */}

        <section
          className="
            rounded-xl

            border
            border-stroke

            bg-white

            p-4

            shadow-card-2

            dark:border-strokedark
            dark:bg-boxdark
            dark:shadow-none

            sm:p-5
          "
        >
          <div className="mb-4">
            <h3 className="font-semibold text-black dark:text-white">
              Información general
            </h3>

            <p className="mt-1 text-xs text-bodydark2 dark:text-bodydark">
              Seleccione el proveedor y la fecha del documento.
            </p>
          </div>

          <QuickAddSupplier
            onReload={onReloadSuppliers}
            onSupplierCreated={onReloadSuppliers}
          />

          <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
            <SimpleSearchSelect
              label="Proveedor"
              name="purchaseSupplier"
              selectedValue={form.supplierId || null}
              iconKey="supplier"
              placeholder="Seleccione un proveedor"
              searchPlaceholder="Buscar proveedor..."
              required
              disabled={loadingOptions || saving}
              error={errors.supplierId}
              options={suppliers.map((supplier) => ({
                id: supplier.id,
                name: supplier.tradeName || supplier.name,
              }))}
              onSelect={(selected) => onChange('supplierId', selected.id)}
            />

            <DatePicker
              label="Fecha de compra"
              name="purchaseDate"
              iconKey="calendar"
              required
              value={form.purchaseDate}
              error={errors.purchaseDate}
              onChange={changePurchaseDate}
            />
          </div>

          <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
            <SimpleSearchSelect
              label="Condición de pago"
              name="purchasePaymentCondition"
              selectedValue={form.paymentCondition?.id ?? null}
              iconKey="payment"
              placeholder="Seleccione una condición"
              searchPlaceholder="Buscar condición..."
              required
              disabled={loadingOptions || saving}
              error={errors.paymentCondition}
              options={paymentConditions.map((condition) => ({
                id: condition.id,
                name: condition.name,
              }))}
              onSelect={(selected) => changePaymentCondition(selected.id)}
            />

            {form.paymentCondition?.code === 'CREDITO' && (
              <SimpleSearchSelect
                label="Unidad del plazo"
                name="purchasePaymentTerm"
                selectedValue={form.paymentCondition.termId}
                iconKey="calendar"
                placeholder="Seleccione días, meses o años"
                searchPlaceholder="Buscar plazo..."
                required
                disabled={loadingOptions || saving}
                error={errors.paymentTerm}
                options={terms.map((term) => ({
                  id: term.id,
                  name: term.name,
                }))}
                onSelect={(selected) => changePaymentTerm(selected.id)}
              />
            )}
          </div>

          {form.paymentCondition?.code === 'CREDITO' && (
            <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
              <InputField
                label={`Cantidad de ${
                  form.paymentCondition.termName?.toLowerCase() || 'plazo'
                }`}
                name="purchasePaymentTermAmount"
                type="number"
                iconKey="calendar"
                required
                min={1}
                step="1"
                disabled={saving || !form.paymentCondition.termCode}
                error={errors.paymentTermAmount}
                value={
                  form.paymentCondition.termCode === '01'
                    ? form.paymentCondition.creditDays ?? ''
                    : form.paymentCondition.termCode === '02'
                    ? form.paymentCondition.creditMonths ?? ''
                    : form.paymentCondition.termCode === '03'
                    ? form.paymentCondition.creditYears ?? ''
                    : ''
                }
                onChange={(event) =>
                  changePaymentTermAmount(
                    event.target.value === '' ? '' : Number(event.target.value)
                  )
                }
              />

              <DatePicker
                label="Fecha de vencimiento"
                name="purchaseCreditDueDate"
                iconKey="calendar"
                required
                value={form.paymentCondition.creditDueDate ?? ''}
                error={errors.creditDueDate}
                onChange={changeCreditDueDate}
              />
            </div>
          )}

          <TextAreaField
            label="Notas"
            name="purchaseNotes"
            value={form.notes}
            rows={3}
            disabled={saving}
            error={errors.notes}
            placeholder="Agregue observaciones, condiciones o referencias de la compra..."
            helperText="Estas notas quedarán asociadas al borrador de la compra."
            rightContent={
              <span
                className={
                  form.notes.length >= 500 ? 'text-danger' : 'text-bodydark2'
                }
              >
                {form.notes.length}/500
              </span>
            }
            onChange={(event) =>
              onChange('notes', event.target.value.slice(0, 500))
            }
          />
        </section>

        {/* =====================================================
            🔹 Detalles de materia prima
        ===================================================== */}

        <section
          className="
            overflow-visible

            rounded-xl

            border
            border-stroke

            bg-white

            shadow-card-2

            dark:border-strokedark
            dark:bg-boxdark
            dark:shadow-none
          "
        >
          <div
            className="
              flex
              flex-col
              gap-3

              border-b
              border-stroke

              bg-gray-2/60

              p-4

              dark:border-strokedark
              dark:bg-meta-4/40

              sm:flex-row
              sm:items-center
              sm:justify-between

              sm:px-5
            "
          >
            <div className="flex items-center gap-3">
              <span
                className="
                  rounded-lg

                  border
                  border-primary/15

                  bg-primary/10

                  p-2

                  text-primary

                  dark:border-primary/25
                  dark:bg-primary/15
                "
              >
                <PackagePlus className="h-5 w-5" />
              </span>

              <div>
                <h3 className="font-semibold text-black dark:text-white">
                  Materias primas
                </h3>

                <p className="mt-0.5 text-xs text-bodydark2 dark:text-bodydark">
                  {form.details.length}{' '}
                  {form.details.length === 1
                    ? 'detalle agregado'
                    : 'detalles agregados'}
                </p>
              </div>
            </div>

            <ActionButton
              label="Agregar detalle"
              icon={<Plus className="h-4 w-4" />}
              variant="primary"
              size="sm"
              fullWidth={false}
              disabled={saving || loadingOptions}
              onClick={onAddDetail}
              className="w-full shrink-0 whitespace-nowrap sm:w-auto"
            />
          </div>

          <div className="space-y-4 p-4 sm:p-5">
            {form.details.length === 0 && (
              <div
                className="
                  flex
                  min-h-[150px]
                  flex-col
                  items-center
                  justify-center

                  rounded-xl

                  border
                  border-dashed
                  border-warning/40

                  bg-warning/5

                  px-5
                  py-8

                  text-center
                "
              >
                <span className="rounded-lg bg-warning/10 p-3 text-warning">
                  <AlertTriangle className="h-6 w-6" />
                </span>

                <p className="mt-3 font-medium text-black dark:text-white">
                  No se han agregado materias primas
                </p>

                <p className="mt-1 text-sm text-warning">{errors.details}</p>
              </div>
            )}

            {form.details.map((detail, index) => {
              const prefix = `detail-${detail.key}`;

              const compatibleUnits = compatiblePurchaseUnits(
                detail.rawMaterialId,
                rawMaterials,
                units,
                conversions
              );

              const selectedMaterial = rawMaterials.find(
                (material) => material.id === Number(detail.rawMaterialId)
              );

              const lineTotal = calculateDetailTotal(detail);

              return (
                <article
                  key={detail.key}
                  className="
                    overflow-visible

                    rounded-xl

                    border
                    border-stroke

                    bg-gray-2/30

                    dark:border-strokedark
                    dark:bg-boxdark-2/60
                  "
                >
                  <div
                    className="
                      flex
                      flex-col
                      gap-3

                      border-b
                      border-stroke

                      px-4
                      py-3

                      dark:border-strokedark

                      sm:flex-row
                      sm:items-center
                      sm:justify-between
                    "
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="
                          flex
                          h-8
                          w-8
                          shrink-0
                          items-center
                          justify-center

                          rounded-lg

                          bg-primary/10

                          text-sm
                          font-bold
                          text-primary
                        "
                      >
                        {index + 1}
                      </span>

                      <div>
                        <p className="text-sm font-semibold text-black dark:text-white">
                          Detalle {index + 1}
                        </p>

                        <p className="mt-0.5 text-xs text-bodydark2 dark:text-bodydark">
                          Complete la cantidad, unidad y costo.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 sm:justify-end">
                      <div className="text-left sm:text-right">
                        <p className="text-xs text-bodydark2 dark:text-bodydark">
                          Total de línea
                        </p>

                        <p className="font-bold text-black dark:text-white">
                          {formatMoney(lineTotal)}
                        </p>
                      </div>

                      <ActionButton
                        label="Eliminar"
                        icon={<Trash2 className="h-4 w-4" />}
                        variant="danger"
                        size="sm"
                        fullWidth={false}
                        disabled={saving}
                        onClick={() => onRemoveDetail(detail.key)}
                        className="shrink-0"
                      />
                    </div>
                  </div>

                  <div
                    className="
                      grid
                      grid-cols-1
                      gap-x-5

                      p-4

                      lg:grid-cols-2
                      xl:grid-cols-4
                    "
                  >
                    <div>
                      <SimpleSearchSelect
                        label="Materia prima"
                        name={`${prefix}-material`}
                        selectedValue={
                          detail.rawMaterialId
                            ? Number(detail.rawMaterialId)
                            : null
                        }
                        iconKey="product"
                        placeholder="Seleccione una materia prima"
                        searchPlaceholder="Buscar materia prima..."
                        required
                        disabled={saving}
                        error={errors[`${prefix}-rawMaterialId`]}
                        options={rawMaterials.map((material) => ({
                          id: material.id,
                          name: `${material.code} · ${material.name}`,
                        }))}
                        onSelect={(selected) =>
                          changeMaterial(detail, selected.id)
                        }
                      />
                      <button
                        type="button"
                        className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50"
                        disabled={saving}
                        onClick={() => setQuickCreateForKey(detail.key)}
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Crear materia prima
                      </button>
                    </div>

                    <InputField
                      label="Cantidad"
                      name={`${prefix}-quantity`}
                      type="number"
                      iconKey="stock"
                      required
                      min={0}
                      step="0.000001"
                      value={detail.quantity}
                      error={errors[`${prefix}-quantity`]}
                      disabled={saving}
                      onChange={(event) =>
                        onDetailChange(
                          detail.key,
                          'quantity',
                          event.target.value === ''
                            ? ''
                            : Number(event.target.value)
                        )
                      }
                    />

                    <SimpleSearchSelect
                      label="Unidad de compra"
                      name={`${prefix}-unit`}
                      selectedValue={
                        detail.unitId ? Number(detail.unitId) : null
                      }
                      iconKey="unitMeasure"
                      placeholder="Seleccione una unidad"
                      searchPlaceholder="Buscar unidad..."
                      required
                      disabled={saving || !detail.rawMaterialId}
                      error={errors[`${prefix}-unitId`]}
                      options={compatibleUnits.map((unit) => ({
                        id: unit.id,
                        name: unit.unitCode
                          ? `${unit.name} (${unit.unitCode})`
                          : unit.name,
                      }))}
                      onSelect={(selected) =>
                        onDetailChange(detail.key, 'unitId', selected.id)
                      }
                    />

                    <InputField
                      label="Costo unitario"
                      name={`${prefix}-cost`}
                      type="number"
                      iconKey="dollar"
                      required
                      min={0}
                      step="0.000001"
                      value={detail.unitCost}
                      error={errors[`${prefix}-unitCost`]}
                      disabled={saving}
                      onChange={(event) =>
                        onDetailChange(
                          detail.key,
                          'unitCost',
                          event.target.value === ''
                            ? ''
                            : Number(event.target.value)
                        )
                      }
                    />

                    <InputField
                      label="Descuento"
                      name={`${prefix}-discount`}
                      type="number"
                      iconKey="discount"
                      min={0}
                      step="0.01"
                      value={detail.discountAmount}
                      error={errors[`${prefix}-discount`]}
                      disabled={saving}
                      onChange={(event) =>
                        onDetailChange(
                          detail.key,
                          'discountAmount',
                          event.target.value === ''
                            ? ''
                            : Number(event.target.value)
                        )
                      }
                    />

                    <InputField
                      label="Impuesto"
                      name={`${prefix}-tax`}
                      type="number"
                      iconKey="dollar"
                      min={0}
                      step="0.01"
                      value={detail.taxAmount}
                      error={errors[`${prefix}-tax`]}
                      disabled={saving}
                      onChange={(event) =>
                        onDetailChange(
                          detail.key,
                          'taxAmount',
                          event.target.value === ''
                            ? ''
                            : Number(event.target.value)
                        )
                      }
                    />

                    <DatePicker
                      label="Vencimiento"
                      name={`${prefix}-expiration`}
                      iconKey="calendar"
                      value={detail.expirationDate}
                      error={errors[`${prefix}-expiration`]}
                      onChange={(date) =>
                        onDetailChange(detail.key, 'expirationDate', date)
                      }
                    />

                    <InputField
                      label="Lote"
                      name={`${prefix}-lot`}
                      type="text"
                      iconKey="code"
                      value={detail.lotNumber}
                      error={errors[`${prefix}-lot`]}
                      disabled={saving}
                      placeholder="Número de lote"
                      onChange={(event) =>
                        onDetailChange(
                          detail.key,
                          'lotNumber',
                          event.target.value.slice(0, 100)
                        )
                      }
                    />
                  </div>

                  <div className="px-4 pb-3">
                    <PurchaseLineConversionHint
                      rawMaterial={selectedMaterial}
                      fromUnitId={detail.unitId}
                      quantity={detail.quantity}
                    />
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* =====================================================
            🔹 Resumen
        ===================================================== */}

        <section
          className="
            flex
            flex-col
            gap-4

            rounded-xl

            border
            border-stroke

            bg-gray-2/50

            p-4

            dark:border-strokedark
            dark:bg-meta-4/20

            sm:flex-row
            sm:items-center
            sm:justify-between

            sm:p-5
          "
        >
          <div className="flex items-center gap-3">
            <span
              className="
                rounded-lg

                border
                border-primary/15

                bg-primary/10

                p-2.5

                text-primary

                dark:border-primary/25
                dark:bg-primary/15
              "
            >
              <Calculator className="h-5 w-5" />
            </span>

            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-bodydark2">
                Total estimado
              </p>

              <p
                className="mt-0.5 text-2xl font-bold text-black dark:text-white"
                aria-live="polite"
              >
                {formatMoney(estimatedTotal)}
              </p>
            </div>
          </div>

          <p className="max-w-md text-xs text-bodydark2 dark:text-bodydark sm:text-right">
            El total definitivo será validado y calculado por el servidor al
            guardar el documento.
          </p>
        </section>

        {/* =====================================================
            🔹 Acciones
        ===================================================== */}

        <div
          className="
            flex
            flex-col-reverse
            gap-3

            border-t
            border-stroke

            pt-5

            dark:border-strokedark

            sm:flex-row
            sm:justify-end
          "
        >
          <ActionButton
            label="Cancelar"
            icon={<X className="h-4 w-4" />}
            variant="secondary"
            size="md"
            fullWidth={false}
            disabled={saving}
            onClick={onClose}
            className="w-full sm:w-auto"
          />

          <ActionButton
            label={form.id ? 'Guardar cambios' : 'Guardar borrador'}
            icon={<Save className="h-4 w-4" />}
            loading={saving}
            loadingLabel="Guardando..."
            variant="primary"
            size="md"
            type="submit"
            fullWidth={false}
            disabled={saving || loadingOptions || hasErrors}
            className="w-full whitespace-nowrap sm:w-auto"
          />
        </div>
      </form>

      <RawMaterialQuickCreateModal
        isOpen={quickCreateForKey !== null}
        categories={categories}
        units={units}
        saving={quickSaving}
        onClose={() => setQuickCreateForKey(null)}
        onSubmit={handleQuickCreate}
        onCreateCategory={onCreateCategory}
      />
    </FormModal>
  );
};

export default RestaurantPurchaseOrderFormModal;
